import { useCallback, useEffect, useMemo, useState } from 'react';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import { useSaveFlow } from '../../components/confirm';
import { useAuthStore } from '../../auth/store';
import { toWire } from '../../utils/datetime';
import { DateRangeSelect } from '../modules/DateRangeSelect';
import { depleteLlts, fetchLltDepletion } from './api';
import { REAGENT_RANGES, rangeCode, rangeDays, type LltDepletionRow, type ReagentDateFilter } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Reagent Preparation → LLT Depletion (nav `rgp-lltdepletion`).
// Port of legacy ALPACA `ReagentPreparation_ListView_LLTDepletion`; spec §16.2.
//
// The list shows only preparations that are NOT yet depleted and are NOT
// Calibration (a fixed server-side criterion, ctrl :488).  Tick the lots that
// are used up, then Save.
//
// TWO DELIBERATE DIFFERENCES FROM LEGACY, both narrowing the failure surface:
//
//  1. Legacy writes DateDepleted / DepletedBy into the row the instant it is
//     ticked (Grid_SelectionChanged), clears them on untick, and only re-checks
//     them at Save — hence spec messages 13 and 14. The grid now SHOWS that the
//     same way (owner request 2026-10-16 — without it this screen looked like
//     LLT Search): a ticked row reads "now" and the signed-in user, an untick
//     clears it. Those are DISPLAY only: the SERVER still stamps both values,
//     inside one transaction, from its own clock and the token's user, so the
//     two messages still cannot occur and nothing typed in the browser is trusted.
//  2. The save is idempotent server-side, so a double-click or a retry cannot
//     re-stamp a lot with a later date.  If every ticked row was already
//     depleted by someone else, the user is told exactly that instead of
//     seeing a silent "Saved successfully.".
//
// Message 12 ("Please select atleast one record to proceed further!") is kept
// verbatim and enforced on BOTH sides — here for UX, in the controller for real
// (§14b.5: the server is authoritative).
// ─────────────────────────────────────────────────────────────────────────────

const COLUMNS: AppColumn<LltDepletionRow>[] = [
  { field: 'llt',            header: 'LLT',            width: 120 },
  // Legacy ColumnInfo Caption — the column shows PrepName under the heading
  // "StandardName". Kept, because that is the wording analysts look for.
  { field: 'prepName',       header: 'StandardName',   width: 220 },
  { field: 'finalVolume',    header: 'FinalVolume',    width: 110 },
  { field: 'prepType',       header: 'PrepType',       width: 110 },
  { field: 'department',     header: 'Department',     width: 120 },
  { field: 'dateDepleted',   header: 'DateDepleted',   width: 150, type: 'datetime' },
  { field: 'depletedBy',     header: 'DepletedBy',     width: 130 },
  { field: 'expirationDate', header: 'ExpirationDate', width: 120, type: 'date' },
  { field: 'datePrepared',   header: 'DatePrepared',   width: 150, type: 'datetime' },
  { field: 'preparedBy',     header: 'PreparedBy',     width: 130 },
];

export default function LltDepletionPage() {
  const notify   = useNotify();
  const runSave  = useSaveFlow();
  const [rows, setRows]       = useState<LltDepletionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  // When each ticked row was ticked — shown as its DateDepleted until Save.
  const [tickedAt, setTickedAt] = useState<Record<string, string>>({});
  const meName = useAuthStore((s) => s.user?.name || s.user?.email || '');

  /** XAF Grid_SelectionChanged: tick → DateDepleted = now, DepletedBy = me; untick → cleared. */
  function onSelect(next: Set<string | number>) {
    setSelected(next);
    setTickedAt((cur) => {
      const out: Record<string, string> = {};
      const now = toWire(new Date());
      for (const id of next) out[String(id)] = cur[String(id)] ?? now;
      return out;
    });
  }

  const shownRows = useMemo(
    () => rows.map((r) => (selected.has(r.oid)
      ? { ...r, dateDepleted: tickedAt[r.oid] ?? r.dateDepleted, depletedBy: meName || r.depletedBy }
      : r)),
    [rows, selected, tickedAt, meName]);
  const [dateFilter, setDateFilter] = useState<ReagentDateFilter | null>(null);

  const load = useCallback(async (filter?: ReagentDateFilter) => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await fetchLltDepletion(filter);
      setRows(res.rows);
      setDateFilter(res.dateFilter);
      // Depleted rows drop out of the list, so any surviving selection would
      // point at rows that are no longer here.
      setSelected(new Set());
      setTickedAt({});
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load LLT depletion.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  async function save() {
    if (selected.size === 0) {
      // Spec §18 message 12, verbatim (including the original spelling).
      notify.warning('Please select atleast one record to proceed further!');
      return;
    }
    const ids = [...selected].map(String);

    // The shared flow gives the ALPACA confirm → blocking loader →
    // "Saved successfully." → reason capture, all for free (§18.6).
    // `successMessage` is a FUNCTION because the wording depends on what the
    // save returned; a plain string would be fixed before `save` ever ran.
    let depleted = 0;
    await runSave({
      formName: 'LLT Depletion',
      auditAction: 'edit',
      confirmMessage: `Mark ${ids.length} lab lot(s) as depleted?`,
      save: async () => { depleted = (await depleteLlts(ids)).depleted; },
      successMessage: () =>
        depleted === 0
          ? 'Those lab lots were already depleted. Nothing changed.'
          : depleted === ids.length
            ? 'Saved successfully.'
            : `Saved successfully. ${depleted} of ${ids.length} marked depleted — the rest were already depleted.`,
      onSaved: () => { void load(dateFilter ?? undefined); },
    });
  }

  return (
    <div className="bl-page">
      <FormTitle pageId="rgp-lltdepletion" loadMs={loadMs} loading={loading}
                 auditFormName="LLT Depletion" />
      <AppToolbar
        left={
          <>
            <button type="button" className="ca-btn" onClick={() => void save()}
                    disabled={loading || selected.size === 0}>
              <i className="ti ti-droplet-off" aria-hidden="true" /> Save
            </button>
            <DateRangeSelect
              variant="dropdown" label="Date Filter" ranges={REAGENT_RANGES}
              value={dateFilter ? rangeDays(dateFilter) : null}
              onChange={(days) => { const c = rangeCode(days); setDateFilter(c); void load(c); }}
            />
          </>
        }
        onRefresh={() => load(dateFilter ?? undefined)}
        refreshing={loading}
        exportData={{ filename: 'llt-depletion', ...gridExportMatrix(rows, COLUMNS) }}
      />
      <AppDataGrid<LltDepletionRow>
        rows={shownRows}
        columns={COLUMNS}
        getRowId={(r) => r.oid}
        loading={loading}
        selectable
        selectedIds={selected}
        onSelectionChange={onSelect}
        emptyText="No lab lots awaiting depletion for this date range."
      />
    </div>
  );
}
